import React, { useState, useEffect } from 'react';
import {
  X,
  Share2,
  ExternalLink,
  Copy,
  Check,
  Plus,
  Settings,
  Sparkles,
  Link as LinkIcon,
  User,
  ShieldCheck,
  AlertCircle,
  Edit2,
  ChevronRight,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Instagram,
  MessageSquare,
  Globe,
  Loader2,
  HelpCircle,
} from 'lucide-react';
import { Mentor, supabase } from '../../lib/supabase';
import { MentorAreaConfig } from '../../types/mentorship';
import { mentorshipService, slugify } from '../../services/mentorshipService';
import { useAuth } from '../../contexts/AuthContext';
import ImageUploader from '../ImageUploader';

interface MentorBioManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  mentors: Mentor[];
  onRefreshMentors: () => Promise<void> | void;
  onOpenConfigureBio: (mentor: Mentor) => void;
  onEditMentorCard?: (mentor: Mentor) => void;
}

export const MentorBioManagerModal: React.FC<MentorBioManagerModalProps> = ({
  isOpen,
  onClose,
  mentors,
  onRefreshMentors,
  onOpenConfigureBio,
  onEditMentorCard,
}) => {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState<'list' | 'wizard'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [fixingSlugs, setFixingSlugs] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Wizard state (3 Passos)
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3>(1);
  const [wizardData, setWizardData] = useState({
    name: '',
    title: '',
    slug: '',
    avatarUrl: '',
    bio: '',
    whatsapp: '',
    instagramUrl: '',
    websiteUrl: '',
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [isSubmittingWizard, setIsSubmittingWizard] = useState(false);

  // Edição rápida de slug inline
  const [editingSlugMentorId, setEditingSlugMentorId] = useState<string | null>(null);
  const [tempSlugValue, setTempSlugValue] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setActiveView('list');
      setWizardStep(1);
      setStatusMessage(null);
      setEditingSlugMentorId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopyLink = (slug: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://masterclub.vip';
    const fullUrl = `${origin}/bio-${slug}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const handleFixAllSlugs = async () => {
    try {
      setFixingSlugs(true);
      const res = await mentorshipService.ensureAllMentorsHaveSlugs();
      setStatusMessage({ text: res.message, type: 'success' });
      await onRefreshMentors();
    } catch (err: any) {
      setStatusMessage({ text: err?.message || 'Erro ao gerar slugs automáticas', type: 'error' });
    } finally {
      setFixingSlugs(false);
    }
  };

  const handleSingleFixSlug = async (mentor: Mentor) => {
    try {
      const generated = slugify(mentor.name) || `mentor-${mentor.id.substring(0, 5)}`;
      const res = await mentorshipService.updateMentorSlug(mentor.id, generated);
      if (res.success) {
        setStatusMessage({ text: `Slug "${generated}" criada para ${mentor.name}!`, type: 'success' });
        await onRefreshMentors();
      } else {
        setStatusMessage({ text: res.message, type: 'error' });
      }
    } catch (err: any) {
      setStatusMessage({ text: 'Erro ao gerar slug', type: 'error' });
    }
  };

  const handleSaveInlineSlug = async (mentorId: string) => {
    if (!tempSlugValue.trim()) return;
    try {
      setSavingSlug(true);
      const res = await mentorshipService.updateMentorSlug(mentorId, tempSlugValue.trim());
      if (res.success) {
        setStatusMessage({ text: res.message, type: 'success' });
        setEditingSlugMentorId(null);
        await onRefreshMentors();
      } else {
        setStatusMessage({ text: res.message, type: 'error' });
      }
    } catch (err: any) {
      setStatusMessage({ text: 'Erro ao atualizar slug', type: 'error' });
    } finally {
      setSavingSlug(false);
    }
  };

  // Funções do Wizard
  const handleWizardNameChange = (name: string) => {
    setWizardData((prev) => ({
      ...prev,
      name,
      slug: slugManuallyEdited ? prev.slug : slugify(name),
    }));
  };

  const handleWizardSlugChange = (val: string) => {
    setSlugManuallyEdited(true);
    setWizardData((prev) => ({
      ...prev,
      slug: slugify(val),
    }));
  };

  const handleCompleteWizard = async () => {
    if (!wizardData.name.trim() || !wizardData.title.trim()) {
      setStatusMessage({ text: 'Por favor, preencha o nome e o cargo/especialidade do mentor.', type: 'error' });
      return;
    }

    const finalSlug = slugify(wizardData.slug || wizardData.name);
    if (!finalSlug) {
      setStatusMessage({ text: 'A slug não pode ser vazia.', type: 'error' });
      return;
    }

    try {
      setIsSubmittingWizard(true);
      setStatusMessage(null);

      const result = await mentorshipService.createMentorWithBio({
        name: wizardData.name,
        title: wizardData.title,
        slug: finalSlug,
        avatar_url: wizardData.avatarUrl,
        bio: wizardData.bio,
        whatsapp: wizardData.whatsapp,
        instagram_url: wizardData.instagramUrl,
        website_url: wizardData.websiteUrl,
        userId: user?.id,
      });

      if (!result.success || !result.mentor) {
        setStatusMessage({ text: result.message, type: 'error' });
        return;
      }

      await onRefreshMentors();
      setStatusMessage({ text: 'Página criada com sucesso! Abrindo configurações da Bio...', type: 'success' });

      // Reset wizard
      setWizardData({
        name: '',
        title: '',
        slug: '',
        avatarUrl: '',
        bio: '',
        whatsapp: '',
        instagramUrl: '',
      });
      setSlugManuallyEdited(false);
      setActiveView('list');

      // Abre automaticamente a configuração da Bio para o mentor criado
      onOpenConfigureBio(result.mentor);
    } catch (err: any) {
      setStatusMessage({ text: err?.message || 'Erro inesperado ao criar mentor.', type: 'error' });
    } finally {
      setIsSubmittingWizard(false);
    }
  };

  const mentorsWithoutSlug = mentors.filter((m) => !m.slug || !m.slug.trim());
  const filteredMentors = mentors.filter((m) => {
    const query = searchQuery.toLowerCase();
    return (
      m.name.toLowerCase().includes(query) ||
      (m.title && m.title.toLowerCase().includes(query)) ||
      (m.slug && m.slug.toLowerCase().includes(query))
    );
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/85 backdrop-blur-md animate-fade-in select-none">
      <div className="relative w-full max-w-4xl bg-[#141518] border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        
        {/* Top Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-gradient-to-r from-[#17191e] to-[#121316]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#0FFF55]/15 border border-[#0FFF55]/30 flex items-center justify-center text-[#0FFF55] shadow-lg shadow-[#0FFF55]/10">
              <Share2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                Gestão de Páginas & Bio Links
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0FFF55]/20 text-[#0FFF55] font-extrabold uppercase tracking-wider">
                  Admin
                </span>
              </h2>
              <p className="text-xs text-gray-400">
                Crie e configure as páginas de Bio (Linktree) e slugs de acesso aos mentores
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status Message Notification */}
        {statusMessage && (
          <div
            className={`px-6 py-3 text-xs font-semibold flex items-center justify-between transition-all ${
              statusMessage.type === 'success'
                ? 'bg-emerald-500/15 border-b border-emerald-500/30 text-emerald-400'
                : 'bg-red-500/15 border-b border-red-500/30 text-red-400'
            }`}
          >
            <div className="flex items-center gap-2">
              {statusMessage.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              ) : (
                <AlertCircle className="w-4 h-4 text-red-400" />
              )}
              <span>{statusMessage.text}</span>
            </div>
            <button
              onClick={() => setStatusMessage(null)}
              className="text-xs text-gray-400 hover:text-white ml-3"
            >
              ✕
            </button>
          </div>
        )}

        {/* Navigation Tabs (Listagem vs Novo Mentor Wizard) */}
        <div className="flex items-center justify-between px-6 pt-4 pb-2 border-b border-white/5 bg-[#121316]">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveView('list')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeView === 'list'
                  ? 'bg-[#0FFF55] text-black shadow-lg shadow-[#0FFF55]/20'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white'
              }`}
            >
              <LinkIcon className="w-3.5 h-3.5" />
              <span>Mentores & Slugs Ativas ({mentors.length})</span>
            </button>

            <button
              onClick={() => {
                setActiveView('wizard');
                setWizardStep(1);
              }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeView === 'wizard'
                  ? 'bg-[#0FFF55] text-black shadow-lg shadow-[#0FFF55]/20'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Plus className="w-3.5 h-3.5" />
              <span>+ Nova Página de Mentor (Guia Passo a Passo)</span>
            </button>
          </div>

          {activeView === 'list' && mentorsWithoutSlug.length > 0 && (
            <button
              onClick={handleFixAllSlugs}
              disabled={fixingSlugs}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold hover:bg-amber-500/20 transition-all"
              title="Gerar slugs automáticas para todos os mentores sem slug"
            >
              {fixingSlugs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              <span>Gerar Slugs Faltantes ({mentorsWithoutSlug.length})</span>
            </button>
          )}
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* ============================================================ */}
          {/* VIZUALIZAÇÃO 1: LISTAGEM DE MENTORES COM SLUGS E AÇÕES       */}
          {/* ============================================================ */}
          {activeView === 'list' && (
            <div className="space-y-4">
              
              {/* Interactive Help Box explaining the workflow */}
              <div className="bg-[#1a1c22] border border-white/10 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-[#0FFF55] text-xs font-extrabold uppercase tracking-wider">
                    <HelpCircle className="w-4 h-4" />
                    <span>Como Funciona o Link da Bio & Slugs</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed max-w-2xl">
                    Cada mentor possui uma <strong className="text-white">slug única</strong> (ex: <code className="text-[#0FFF55] bg-black/40 px-1 py-0.5 rounded">vitor-terra</code>).
                    Isso cria automaticamente a página pública do Linktree em{' '}
                    <span className="text-white font-mono text-[11px]">masterclub.vip/bio-{'{slug}'}</span> e a Área de Membros em{' '}
                    <span className="text-white font-mono text-[11px]">masterclub.vip/mentoria-{'{slug}'}</span>.
                  </p>
                </div>

                <button
                  onClick={() => {
                    setActiveView('wizard');
                    setWizardStep(1);
                  }}
                  className="whitespace-nowrap px-4 py-2.5 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs flex items-center justify-center gap-1.5 shadow-lg shadow-[#0FFF55]/15 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>Criar Nova Slug</span>
                </button>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Pesquisar mentor por nome, cargo ou slug..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 bg-[#111215] border border-white/10 rounded-2xl text-xs text-white placeholder-gray-500 focus:outline-none focus:border-[#0FFF55] transition-all"
                />
              </div>

              {/* Mentors Table / Cards */}
              <div className="space-y-3">
                {filteredMentors.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-white/10 rounded-2xl">
                    <User className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-300 font-bold">Nenhum mentor encontrado</p>
                    <p className="text-xs text-gray-500 mt-1">Crie um novo mentor ou ajuste a busca acima.</p>
                  </div>
                ) : (
                  filteredMentors.map((mentor) => {
                    const hasSlug = !!mentor.slug && mentor.slug.trim().length > 0;
                    const cleanSlug = hasSlug ? mentor.slug! : '';
                    const isCopied = copiedSlug === cleanSlug;
                    const isEditingThisSlug = editingSlugMentorId === mentor.id;

                    return (
                      <div
                        key={mentor.id}
                        className="bg-[#121316] border border-white/5 hover:border-white/15 rounded-2xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 transition-all"
                      >
                        {/* Avatar & Informações do Mentor */}
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className="relative w-12 h-12 rounded-full overflow-hidden border border-white/10 bg-[#17181c] flex-shrink-0 flex items-center justify-center">
                            {mentor.avatar_url ? (
                              <img
                                src={mentor.avatar_url}
                                alt={mentor.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <User className="w-6 h-6 text-gray-500" />
                            )}
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-bold text-white truncate">{mentor.name}</h3>
                              {hasSlug ? (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-mono border border-emerald-500/20">
                                  /bio-{cleanSlug}
                                </span>
                              ) : (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-bold border border-amber-500/20">
                                  ⚠️ Sem Slug
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 truncate">{mentor.title || 'Sem título cadastrado'}</p>

                            {/* Slug editor inline */}
                            {isEditingThisSlug ? (
                              <div className="flex items-center gap-1.5 mt-2">
                                <input
                                  type="text"
                                  value={tempSlugValue}
                                  onChange={(e) => setTempSlugValue(slugify(e.target.value))}
                                  placeholder="digite-a-slug"
                                  className="px-2.5 py-1 bg-black border border-[#0FFF55] rounded-lg text-xs text-white font-mono"
                                  autoFocus
                                />
                                <button
                                  onClick={() => handleSaveInlineSlug(mentor.id)}
                                  disabled={savingSlug}
                                  className="px-2.5 py-1 rounded-lg bg-[#0FFF55] text-black text-xs font-bold hover:bg-[#0dd149]"
                                >
                                  {savingSlug ? '...' : 'Salvar'}
                                </button>
                                <button
                                  onClick={() => setEditingSlugMentorId(null)}
                                  className="px-2 py-1 text-gray-400 hover:text-white text-xs"
                                >
                                  Cancelar
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {/* Ações Rápidas */}
                        <div className="flex flex-wrap items-center gap-2 self-stretch md:self-auto justify-end border-t md:border-t-0 pt-3 md:pt-0 border-white/5">
                          {hasSlug ? (
                            <>
                              {/* Botão Copiar URL */}
                              <button
                                onClick={() => handleCopyLink(cleanSlug)}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-200 hover:text-white text-xs font-semibold transition-all"
                                title="Copiar link da bio"
                              >
                                {isCopied ? <Check className="w-3.5 h-3.5 text-[#0FFF55]" /> : <Copy className="w-3.5 h-3.5" />}
                                <span>{isCopied ? 'Copiado!' : 'Copiar Bio'}</span>
                              </button>

                              {/* Botão Visualizar Bio */}
                              <a
                                href={`/bio-${cleanSlug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-200 hover:text-white text-xs font-semibold transition-all"
                                title="Abrir página pública da Bio"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                <span>Ver Bio</span>
                              </a>

                              {/* Botão Configurar Bio */}
                              <button
                                onClick={() => onOpenConfigureBio(mentor)}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-extrabold transition-all shadow-md shadow-[#0FFF55]/10"
                                title="Editar links, redes sociais e layout da bio"
                              >
                                <Settings className="w-3.5 h-3.5" />
                                <span>Configurar Bio</span>
                              </button>

                              {/* Botão Editar Card da Vitrine */}
                              {onEditMentorCard && (
                                <button
                                  onClick={() => onEditMentorCard(mentor)}
                                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-200 hover:text-white text-xs font-semibold transition-all border border-white/5"
                                  title="Editar fotos (avatar, banner) e dados do card da vitrine"
                                >
                                  <Edit2 className="w-3.5 h-3.5 text-[#0FFF55]" />
                                  <span>Editar Card</span>
                                </button>
                              )}

                              {/* Botão Editar Slug */}
                              <button
                                onClick={() => {
                                  setEditingSlugMentorId(mentor.id);
                                  setTempSlugValue(cleanSlug);
                                }}
                                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                                title="Alterar slug"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleSingleFixSlug(mentor)}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 text-xs font-bold transition-all"
                            >
                              <Sparkles className="w-3.5 h-3.5" />
                              <span>✨ Gerar Slug para este Mentor</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* VIZUALIZAÇÃO 2: GUIA PASSO A PASSO (WIZARD DE NOVO MENTOR)   */}
          {/* ============================================================ */}
          {activeView === 'wizard' && (
            <div className="space-y-6 max-w-2xl mx-auto">
              
              {/* Stepper Header Visual */}
              <div className="grid grid-cols-3 gap-2 pb-2">
                <div
                  onClick={() => setWizardStep(1)}
                  className={`p-3 rounded-2xl border transition-all cursor-pointer ${
                    wizardStep === 1
                      ? 'bg-[#0FFF55]/10 border-[#0FFF55] text-white'
                      : wizardStep > 1
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                      : 'bg-white/5 border-white/5 text-gray-500'
                  }`}
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider">Passo 1</div>
                  <div className="text-xs font-extrabold mt-0.5">Gerar a Slug</div>
                </div>

                <div
                  onClick={() => wizardStep >= 2 && setWizardStep(2)}
                  className={`p-3 rounded-2xl border transition-all ${
                    wizardStep === 2
                      ? 'bg-[#0FFF55]/10 border-[#0FFF55] text-white cursor-pointer'
                      : wizardStep > 2
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 cursor-pointer'
                      : 'bg-white/5 border-white/5 text-gray-500 opacity-60'
                  }`}
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider">Passo 2</div>
                  <div className="text-xs font-extrabold mt-0.5">Inicializar a Bio</div>
                </div>

                <div
                  onClick={() => wizardStep === 3 && setWizardStep(3)}
                  className={`p-3 rounded-2xl border transition-all ${
                    wizardStep === 3
                      ? 'bg-[#0FFF55]/10 border-[#0FFF55] text-white'
                      : 'bg-white/5 border-white/5 text-gray-500 opacity-60'
                  }`}
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider">Passo 3</div>
                  <div className="text-xs font-extrabold mt-0.5">Publicar & Editar</div>
                </div>
              </div>

              {/* PASSO 1: DEFINIR SLUG & DADOS BÁSICOS */}
              {wizardStep === 1 && (
                <div className="bg-[#121316] border border-white/10 rounded-2xl p-6 space-y-5">
                  <div className="border-b border-white/5 pb-4">
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#0FFF55] text-black font-extrabold text-xs flex items-center justify-center">
                        1
                      </span>
                      Defina a Identidade e a Slug do Mentor
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">
                      A slug é o identificador único na URL (ex: <code className="text-[#0FFF55]">lucas-silva</code>).
                      Ela é gerada automaticamente pelo nome, mas você pode personalizá-la.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-300 mb-1.5">
                        Nome Completo do Mentor <span className="text-[#0FFF55]">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Ex: Lucas Silva"
                        value={wizardData.name}
                        onChange={(e) => handleWizardNameChange(e.target.value)}
                        className="w-full px-4 py-3 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-300 mb-1.5">
                        Título / Especialidade do Mentor <span className="text-[#0FFF55]">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Ex: Engenheiro de IA & Fundador da TechAI"
                        value={wizardData.title}
                        onChange={(e) => setWizardData({ ...wizardData, title: e.target.value })}
                        className="w-full px-4 py-3 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-bold text-gray-300">
                          Slug da Página (URL única) <span className="text-[#0FFF55]">*</span>
                        </label>
                        <span className="text-[10px] text-gray-500 font-mono">somente letras minúsculas e hífens</span>
                      </div>
                      <div className="flex items-center">
                        <span className="px-3 py-3 bg-[#1b1c22] border border-r-0 border-white/15 rounded-l-xl text-xs text-gray-400 font-mono">
                          /bio-
                        </span>
                        <input
                          type="text"
                          placeholder="lucas-silva"
                          value={wizardData.slug}
                          onChange={(e) => handleWizardSlugChange(e.target.value)}
                          className="flex-1 px-4 py-3 bg-[#17181c] border border-white/15 rounded-r-xl text-xs text-white font-mono focus:outline-none focus:border-[#0FFF55]"
                        />
                      </div>
                    </div>

                    {/* Live URL Preview Box */}
                    <div className="bg-black/40 border border-white/10 rounded-xl p-3.5 space-y-1">
                      <span className="text-[10px] font-bold text-[#0FFF55] uppercase tracking-wider">
                        Prévia dos Links que Serão Gerados:
                      </span>
                      <div className="text-xs text-gray-300 font-mono">
                        🌐 Link da Bio: <span className="text-white">https://masterclub.vip/bio-{wizardData.slug || 'sua-slug'}</span>
                      </div>
                      <div className="text-xs text-gray-400 font-mono">
                        🎓 Área de Membros: <span className="text-gray-300">https://masterclub.vip/mentoria-{wizardData.slug || 'sua-slug'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-between items-center border-t border-white/5">
                    <button
                      type="button"
                      onClick={() => setActiveView('list')}
                      className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 text-xs font-bold"
                    >
                      Cancelar
                    </button>

                    <button
                      type="button"
                      disabled={!wizardData.name.trim() || !wizardData.title.trim()}
                      onClick={() => setWizardStep(2)}
                      className="px-6 py-2.5 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] disabled:opacity-40 text-black font-extrabold text-xs flex items-center gap-2 transition-all shadow-lg shadow-[#0FFF55]/20"
                    >
                      <span>Avançar para o Passo 2: Inicializar Bio</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* PASSO 2: INICIALIZAR A BIO */}
              {wizardStep === 2 && (
                <div className="bg-[#121316] border border-white/10 rounded-2xl p-6 space-y-5">
                  <div className="border-b border-white/5 pb-4">
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#0FFF55] text-black font-extrabold text-xs flex items-center justify-center">
                        2
                      </span>
                      Inicialize o Perfil da Bio & Canais
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">
                      Configure a foto de perfil, bio de apresentação e links rápidos de contato do mentor.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <ImageUploader
                        bucketName="mentorias"
                        label="Foto do Mentor / Avatar (Arraste ou Selecione o Arquivo)"
                        value={wizardData.avatarUrl}
                        onChange={(url) => setWizardData((prev) => ({ ...prev, avatarUrl: url }))}
                        options={{ maxWidth: 400, maxHeight: 400, quality: 0.85, format: 'webp' }}
                        placeholder="Arraste e solte a foto do mentor aqui (Drag & Drop)..."
                      />
                      <div className="bg-[#0FFF55]/5 border border-[#0FFF55]/20 rounded-xl p-3 mt-2.5 flex items-start gap-2.5">
                        <Sparkles className="w-4 h-4 text-[#0FFF55] flex-shrink-0 mt-0.5" />
                        <span className="text-[11px] text-gray-300 leading-relaxed">
                          <strong>Dica & Flexibilidade:</strong> A foto é <strong className="text-white">opcional</strong>. Se você não tiver o arquivo da foto agora, basta prosseguir — o sistema utilizará um avatar padrão provisório. Você poderá alterá-la a qualquer momento!
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-300 mb-1.5">
                        Frase de Bio / Apresentação Rápida
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Ex: Acelerando carreiras com Engenharia de Inteligência Artificial."
                        value={wizardData.bio}
                        onChange={(e) => setWizardData({ ...wizardData, bio: e.target.value })}
                        className="w-full px-4 py-3 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-300 mb-1.5 flex items-center gap-1.5">
                          <MessageSquare className="w-3.5 h-3.5 text-[#25D366]" /> WhatsApp de Contato
                        </label>
                        <input
                          type="text"
                          placeholder="5511999999999"
                          value={wizardData.whatsapp}
                          onChange={(e) => setWizardData({ ...wizardData, whatsapp: e.target.value })}
                          className="w-full px-4 py-3 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-300 mb-1.5 flex items-center gap-1.5">
                          <Instagram className="w-3.5 h-3.5 text-[#E4405F]" /> Perfil do Instagram
                        </label>
                        <input
                          type="text"
                          placeholder="https://instagram.com/perfil"
                          value={wizardData.instagramUrl}
                          onChange={(e) => setWizardData({ ...wizardData, instagramUrl: e.target.value })}
                          className="w-full px-4 py-3 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-300 mb-1.5 flex items-center gap-1.5">
                          <Globe className="w-3.5 h-3.5 text-[#0FFF55]" /> Website / Site Oficial
                        </label>
                        <input
                          type="text"
                          placeholder="https://seusite.com"
                          value={wizardData.websiteUrl}
                          onChange={(e) => setWizardData({ ...wizardData, websiteUrl: e.target.value })}
                          className="w-full px-4 py-3 bg-[#17181c] border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-[#0FFF55]"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-between items-center border-t border-white/5">
                    <button
                      type="button"
                      onClick={() => setWizardStep(1)}
                      className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 text-xs font-bold flex items-center gap-1.5"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Voltar</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setWizardStep(3)}
                      className="px-6 py-2.5 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs flex items-center gap-2 transition-all shadow-lg shadow-[#0FFF55]/20"
                    >
                      <span>Avançar para o Passo 3: Confirmação</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* PASSO 3: CONFIRMAÇÃO & ABRIR EDITOR DE BIO */}
              {wizardStep === 3 && (
                <div className="bg-[#121316] border border-white/10 rounded-2xl p-6 space-y-5">
                  <div className="border-b border-white/5 pb-4">
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#0FFF55] text-black font-extrabold text-xs flex items-center justify-center">
                        3
                      </span>
                      Confirme e Abra o Editor da Bio
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">
                      Ao criar a página, o sistema gravará o mentor com a nova slug no banco de dados e abrirá
                      imediatamente o configurador de links e redes sociais para você.
                    </p>
                  </div>

                  {/* Resumo Card */}
                  <div className="bg-black/50 border border-white/10 rounded-2xl p-5 space-y-4">
                    <div className="flex items-center gap-3.5">
                      <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#0FFF55] bg-[#121316] flex-shrink-0 flex items-center justify-center">
                        {wizardData.avatarUrl ? (
                          <img
                            src={wizardData.avatarUrl}
                            alt="Avatar"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-7 h-7 text-[#0FFF55]" />
                        )}
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-white">{wizardData.name}</h4>
                        <p className="text-xs text-[#0FFF55] font-semibold">{wizardData.title}</p>
                        <p className="text-[11px] text-gray-400 font-mono mt-0.5">
                          Slug Oficial: <strong>{slugify(wizardData.slug || wizardData.name)}</strong>
                        </p>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-white/10 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">Página Pública de Bio:</span>
                        <span className="text-white font-mono bg-white/5 px-2 py-0.5 rounded">
                          masterclub.vip/bio-{slugify(wizardData.slug || wizardData.name)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">Área de Membros Hubla:</span>
                        <span className="text-white font-mono bg-white/5 px-2 py-0.5 rounded">
                          masterclub.vip/mentoria-{slugify(wizardData.slug || wizardData.name)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-between items-center border-t border-white/5">
                    <button
                      type="button"
                      onClick={() => setWizardStep(2)}
                      disabled={isSubmittingWizard}
                      className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 text-xs font-bold flex items-center gap-1.5"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Voltar</span>
                    </button>

                    <button
                      type="button"
                      disabled={isSubmittingWizard}
                      onClick={handleCompleteWizard}
                      className="px-6 py-3 rounded-xl bg-[#0FFF55] hover:bg-[#0dd149] text-black font-extrabold text-xs flex items-center gap-2 transition-all shadow-xl shadow-[#0FFF55]/25 active:scale-95"
                    >
                      {isSubmittingWizard ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Criando Página & Inicializando Bio...</span>
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Criar Página & Abrir Editor de Bio Agora</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
